import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch } from '@/lib/api';
import { useDerivedSelection } from '@/hooks/use-derived-state';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FileUp, Plus, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Gated } from '@/auth/permissions';
import { bankWriteErrorMessage, useBankEditGuard } from '@/hooks/useBankEditGuard';
import QuestionBankBulkUpload from '@/components/QuestionBankBulkUpload';
import QuestionBankReadOnlyBanner from '@/components/QuestionBankReadOnlyBanner';
import UnsavedRefreshGuard from '@/components/UnsavedRefreshGuard';
import {
  QuestionBankBulkDeleteBar,
  QuestionEditorDeleteButton,
  QuestionRowCheckbox,
  useCheckedQuestionIds,
} from '@/components/QuestionBankDeleteControls';

const EDIT_PERM = 'bank.videobot.edit';
const API_PATH = '/questions/videobot';
const QUERY_KEY = ['v1-videobot-qs'];

const STATUSES = ['draft', 'active', 'retired'];
const FOCUS_OPTIONS = ['Structure', 'Relevance', 'Confidence', 'Conciseness'];

const DEFAULT_FRAMEWORK = {
  key_concepts: [],
  acceptable_variants: [],
  red_flags: [],
  layer_weights: { layer1: 40, layer2: 60 },
  primary_focus: ['Structure', 'Relevance'],
};

const DEFAULT_NEW_TAGS = 'SE_Dev';

function isNewFormDirty(text, tags) {
  return text.trim() !== '' || tags.trim() !== DEFAULT_NEW_TAGS;
}

function parseTagNames(text) {
  return text.split(',').map((t) => t.trim()).filter(Boolean);
}

function sameStringList(a, b) {
  const aa = [...a].sort();
  const bb = [...b].sort();
  return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

function sameArray(a, b) {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function isVideoBotEditorDirty(q, state) {
  const fw = q.framework || DEFAULT_FRAMEWORK;
  const savedLayer2 = fw.layer_weights?.layer2 ?? 50;
  const savedTags = (q.tags || []).map((t) => t.name);
  return state.text !== q.text
    || state.prep !== q.prep_seconds
    || state.rec !== q.record_seconds
    || state.retakes !== q.retake_attempts
    || state.status !== q.status
    || state.layer2Weight !== savedLayer2
    || !sameArray(state.concepts, fw.key_concepts || [])
    || !sameArray(state.variants, fw.acceptable_variants || [])
    || !sameArray(state.flags, fw.red_flags || [])
    || !sameArray(state.focus, fw.primary_focus || [])
    || !sameStringList(savedTags, parseTagNames(state.tagsText));
}

export default function VideoBotBank() {
  const qc = useQueryClient();
  const { readOnly, denyEdit, guard } = useBankEditGuard(EDIT_PERM);
  const { data: qs } = useQuery({ queryKey: QUERY_KEY, queryFn: () => v1Get(API_PATH) });
  const [selected, setSelected] = useDerivedSelection(qs);
  const [newOpen, setNewOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [newText, setNewText] = useState('');
  const [newTags, setNewTags] = useState(DEFAULT_NEW_TAGS);
  const { checked, toggle, clear, setAll } = useCheckedQuestionIds();

  if (!qs) return null;

  const resetNewForm = () => {
    setNewText('');
    setNewTags(DEFAULT_NEW_TAGS);
  };

  const handleNewDialogOpenChange = (open) => {
    if (!open) {
      if (isNewFormDirty(newText, newTags)) {
        setNewOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetNewForm();
    }
    setNewOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isNewFormDirty(newText, newTags)) {
        setNewOpen(true);
      }
    }
  };

  const confirmDiscardNewForm = () => {
    discardConfirmedRef.current = true;
    resetNewForm();
    setNewOpen(false);
  };

  const createQ = async () => {
    if (readOnly) return denyEdit();
    if (!newText.trim()) return toast.error('Question text required');
    try {
      const created = await v1Post(API_PATH, {
        text: newText,
        framework: { ...DEFAULT_FRAMEWORK, key_concepts: ['placeholder concept'] },
        prep_seconds: 30,
        record_seconds: 90,
        retake_attempts: 0,
        status: 'draft',
        tag_names: newTags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      setSelected(created);
      setNewOpen(false);
      resetNewForm();
      toast.success('Question draft created — fill in the framework before activating.');
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Failed to create question'));
    }
  };

  const afterDelete = (ids) => {
    const gone = new Set(Array.isArray(ids) ? ids : [ids]);
    clear();
    qc.invalidateQueries({ queryKey: QUERY_KEY });
    if (selected && gone.has(selected.id)) setSelected(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Video Bot Question Bank"
        subtitle="Each question requires an answer framework: key concepts, optional variants, red-flags. Layer weights must sum to 100."
        actions={(
          <Gated need={EDIT_PERM}>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="gap-1.5" onClick={() => setBulkOpen(true)} data-testid="videobot-bulk-upload-button">
                <FileUp className="h-4 w-4" /> Bulk upload
              </Button>
              <Button className="gap-1.5" onClick={() => setNewOpen(true)} data-testid="new-videobot-q-button"><Plus className="h-4 w-4" /> New question</Button>
            </div>
          </Gated>
        )}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3" data-testid="question-list">
          <p className="shrink-0 px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Questions ({qs.length})</p>
          <div className="shrink-0">
            <QuestionBankBulkDeleteBar
              ids={qs.map((q) => q.id)}
              checked={checked}
              toggle={toggle}
              setAll={setAll}
              clear={clear}
              apiPath={API_PATH}
              editPermission={EDIT_PERM}
              onDeleted={afterDelete}
              testIdPrefix="vb-bank"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {qs.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">No questions yet — click <span className="font-medium">New question</span> to add the first one.</p>
            ) : (
              <ul className="space-y-1">
                {qs.map((q) => (
                  <li key={q.id} className="flex items-start gap-2">
                    <QuestionRowCheckbox
                      id={q.id}
                      checked={checked.has(q.id)}
                      onToggle={toggle}
                      editPermission={EDIT_PERM}
                      testId={`vb-check-${q.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setSelected(q)}
                      className={`flex min-w-0 flex-1 flex-col items-start gap-1 rounded-lg px-3 py-2 text-left text-sm transition-colors ${selected?.id === q.id ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                      data-testid={`vb-list-item-${q.id}`}
                    >
                      <span className="line-clamp-2">{q.text}</span>
                      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground capitalize">
                        {q.status} · {(q.tags || []).map((t) => t.name).join(', ') || '—'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent></Card>
        {selected && (
          <VideoBotEditor
            q={selected}
            key={selected.id}
            readOnly={readOnly}
            guard={guard}
            onChange={() => qc.invalidateQueries({ queryKey: QUERY_KEY })}
            onDeleted={() => afterDelete(selected.id)}
          />
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={handleNewDialogOpenChange}>
        <DialogContent data-testid="new-videobot-q-dialog">
          <DialogHeader>
            <DialogTitle>New Video Bot question</DialogTitle>
            <DialogDescription>Drafts can be edited and assigned an answer framework after creation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Question text</label>
              <Textarea rows={3} value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="Walk us through how you would scale a write-heavy system..." data-testid="vb-new-text" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tags (comma-separated)</label>
              <Input value={newTags} onChange={(e) => setNewTags(e.target.value)} placeholder="e.g. SE_Dev, SSE_Dev" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => handleNewDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={createQ} data-testid="new-videobot-q-confirm">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="new-videobot-q-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered question data will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="new-videobot-q-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardNewForm} data-testid="new-videobot-q-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedRefreshGuard
        armed={newOpen && isNewFormDirty(newText, newTags)}
        testIdPrefix="new-videobot-q"
      />

      <QuestionBankBulkUpload
        kind="videobot"
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={() => {
          qc.invalidateQueries({ queryKey: QUERY_KEY });
        }}
      />
    </div>
  );
}

function VideoBotEditor({ q, readOnly, guard, onChange, onDeleted }) {
  const fw = q.framework || DEFAULT_FRAMEWORK;
  const [text, setText] = useState(q.text);
  const [prep, setPrep] = useState(q.prep_seconds);
  const [rec, setRec] = useState(q.record_seconds);
  const [retakes, setRetakes] = useState(q.retake_attempts);
  const [concepts, setConcepts] = useState(fw.key_concepts || []);
  const [variants, setVariants] = useState(fw.acceptable_variants || []);
  const [flags, setFlags] = useState(fw.red_flags || []);
  const [layer2Weight, setLayer2Weight] = useState(fw.layer_weights?.layer2 ?? 50);
  const [focus, setFocus] = useState(fw.primary_focus || []);
  const [status, setStatus] = useState(q.status);
  const [tagsText, setTagsText] = useState((q.tags || []).map((t) => t.name).join(', '));
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(
    () => isVideoBotEditorDirty(q, {
      text, prep, rec, retakes, status, layer2Weight, concepts, variants, flags, focus, tagsText,
    }),
    [q, text, prep, rec, retakes, status, layer2Weight, concepts, variants, flags, focus, tagsText],
  );

  const save = guard(async () => {
    if (saving || !dirty) return;
    if (concepts.length === 0) return toast.error('Add at least one key concept.');
    if (concepts.length > 5) return toast.error('At most 5 key concepts.');
    if (variants.length > 3) return toast.error('At most 3 acceptable variants.');
    if (flags.length > 5) return toast.error('At most 5 red flags.');
    setSaving(true);
    try {
      const framework = {
        key_concepts: concepts,
        acceptable_variants: variants,
        red_flags: flags,
        layer_weights: { layer1: 100 - layer2Weight, layer2: layer2Weight },
        primary_focus: focus,
      };
      await v1Patch(`${API_PATH}/${q.id}`, {
        text,
        framework,
        prep_seconds: prep,
        record_seconds: rec,
        retake_attempts: retakes,
        status,
        tag_names: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
      });
      toast.success('Question saved');
      onChange?.();
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6" data-testid="question-editor-form">
      {readOnly && <QuestionBankReadOnlyBanner testId="vb-editor-read-only" />}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Editing Video Bot question</p>
        </div>
        <Select value={status} onValueChange={setStatus} disabled={readOnly}>
          <SelectTrigger className="w-32" data-testid="vb-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Question text</label>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={readOnly} data-testid="vb-editor-text" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div><label className="text-xs font-medium text-muted-foreground">Prep seconds</label><Input type="number" value={prep} onChange={(e) => setPrep(Number(e.target.value))} disabled={readOnly} /></div>
        <div><label className="text-xs font-medium text-muted-foreground">Record seconds</label><Input type="number" value={rec} onChange={(e) => setRec(Number(e.target.value))} disabled={readOnly} /></div>
        <div><label className="text-xs font-medium text-muted-foreground">Retake attempts</label><Input type="number" value={retakes} onChange={(e) => setRetakes(Number(e.target.value))} disabled={readOnly} /></div>
      </div>
      <ChipEditor title="Key concepts (1–5)" items={concepts} setItems={setConcepts} placeholder="e.g., peer review process" testid="key-concepts" readOnly={readOnly} guard={guard} />
      <ChipEditor title="Acceptable answer variants (max 3)" items={variants} setItems={setVariants} placeholder="e.g., formal PR review" testid="variants" readOnly={readOnly} guard={guard} />
      <ChipEditor title="Red-flag indicators (max 5)" items={flags} setItems={setFlags} placeholder="e.g., conflates code review with testing" testid="red-flags" readOnly={readOnly} guard={guard} />
      <div>
        <div className="flex items-center justify-between"><label className="text-xs font-medium text-muted-foreground">Layer weights</label><span className="text-xs text-muted-foreground tabular-nums">L1 {100 - layer2Weight}% / L2 {layer2Weight}%</span></div>
        <Slider min={0} max={100} step={5} value={[layer2Weight]} onValueChange={(v) => setLayer2Weight(v[0])} disabled={readOnly} className="mt-2" data-testid="vb-layer-slider" />
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Primary evaluation focus</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {FOCUS_OPTIONS.map((d) => {
            const active = focus.includes(d);
            return <button key={d} type="button" onClick={guard(() => setFocus(active ? focus.filter((x) => x !== d) : [...focus, d]))} className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-accent'} ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`} data-testid={`vb-focus-${d}`}>{d}</button>;
          })}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Tags (comma-separated)</label>
        <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} disabled={readOnly} data-testid="vb-editor-tags" />
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <QuestionEditorDeleteButton
          apiPath={API_PATH}
          questionId={q.id}
          questionPreview={text}
          editPermission={EDIT_PERM}
          onDeleted={onDeleted}
          testId="vb-editor-delete"
        />
        <Button onClick={save} disabled={saving || readOnly || !dirty} className="gap-1.5" data-testid="vb-editor-save"><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save question'}</Button>
      </div>
    </CardContent></Card>
  );
}

function ChipEditor({ title, items, setItems, placeholder, testid, readOnly, guard }) {
  const [val, setVal] = useState('');
  return (
    <div data-testid={testid}>
      <label className="text-xs font-medium text-muted-foreground">{title}</label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {items.map((c, idx) => (
          <span key={`${c}-${idx}`} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs">{c}<button type="button" onClick={guard(() => setItems(items.filter((_, i) => i !== idx)))} className="text-muted-foreground"><X className="h-3 w-3" /></button></span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} disabled={readOnly} />
        <Button
          variant="outline"
          disabled={readOnly}
          onClick={guard(() => {
            if (val.trim()) {
              setItems([...items, val.trim()]);
              setVal('');
            }
          })}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
